import { HUB_LOGIN_PATH, HUB_MCP_CONNECT_PATH, HUB_REGISTER_PATH } from '../routes/paths';
import { SESSION_KEY_PARAM, SESSION_KEY_RE, persistSessionKey, type LocationLike } from './auth';
import { ensureHostInfo } from './host';
import { apiUrlFetch } from './http';

/**
 * UX-802 / UX-803 / API-506 — "Sign in with Google" (login page) and "Sign up with Google"
 * (registration page).
 *
 * A FULL-PAGE AUTHORIZATION-CODE REDIRECT, NOT Google Identity Services. Brands serve the login
 * page on many custom domains, and GSI's in-page button / One Tap only works on JavaScript origins
 * registered in the Google Cloud console — which cannot be kept in step with every brand's domain.
 * The API owns the Google round trip instead; this module only starts it and reads the result the
 * API hands back. Do not load Google's GSI script.
 *
 *   1. START. A fresh random nonce is stored in THIS TAB's sessionStorage (`hdph.googleSsoNonce`),
 *      then a full navigation to
 *        sign in: `{apiHost}/Auth/google/start?brandId=<id>&returnHost=<host>&ssoNonce=<nonce>`
 *        sign up: the same, plus `&intent=signup`
 *      Sign-in URLs carry no `intent` at all — byte-identical to the UX-802 contract.
 *      The browser sends only a HOSTNAME (`window.location.host`, lowercased, port included when
 *      there is one — `photos.brand.com`, or `localhost:3000` in dev), never a URL: the API builds
 *      the return URL itself from the intent, so no caller-supplied path or query ever reaches the
 *      redirect:
 *        sign in → `https://{returnHost}/ux/login`
 *        sign up → `https://{returnHost}/ux/register`
 *   2. Google consent screen.
 *   3. RETURN. The API redirects to that page with the result in the URL FRAGMENT:
 *        `#sso=<outcome>&ssoNonce=<n>`, plus `&SessionKey=<32 hex>` for `success` and `created`.
 *      Outcomes: `success` (signed in to an existing account), `created` (sign up made a new
 *      account), `no-account` (sign in found none), `email-in-use` (sign up hit an address that
 *      cannot simply be signed in), `suspended` (ROBO-481: the account's business is suspended and
 *      its user type may not sign in — the Google counterpart of a 403 `BRAND_SUSPENDED`; a
 *      suspended business's Owner gets `success` instead), `failed` (also covers the user
 *      cancelling at Google). Anything else is `failed`.
 *
 * WHY THE NONCE (security audit). Without it, anyone could send a victim a link carrying the
 * attacker's own session and sign the victim into the attacker's account (login CSRF). A return is
 * accepted only when its `ssoNonce` equals the nonce this tab issued; anything else is `failed` and
 * its SessionKey is discarded unpersisted.
 *
 * WHY THE FRAGMENT. A fragment is never sent to a server, so the session id stays out of access logs
 * and `Referer` headers — the query string put it in both.
 */

export const GOOGLE_START_PATH = 'Auth/google/start';

/** The result parameter, in the return fragment. */
export const SSO_PARAM = 'sso';

/** The nonce parameter — sent on start, echoed back in the return fragment. */
export const SSO_NONCE_PARAM = 'ssoNonce';

/** The intent parameter — sent on start for sign up only. */
export const SSO_INTENT_PARAM = 'intent';

/** sessionStorage key for the nonce. sessionStorage is per tab, which is the point. */
export const GOOGLE_SSO_NONCE_KEY = 'hdph.googleSsoNonce';

const SSO_NONCE_BYTES = 32;

/** Which page started the round trip, and therefore which page the API returns to. */
export type GoogleSsoIntent = 'signin' | 'signup';

export type SsoOutcome =
    | 'success'
    | 'no-account'
    | 'created'
    | 'email-in-use'
    | 'suspended'
    | 'failed';

/** ★ Must match the API's `GoogleSsoReturnHost.SSO_*` values (pinned in `GoogleSsoReturnHostTests`). */
const SSO_OUTCOMES: readonly SsoOutcome[] = [
    'success',
    'no-account',
    'created',
    'email-in-use',
    'suspended',
    'failed'
];

/** Outcomes that carry a SessionKey to persist. */
const SESSION_OUTCOMES: readonly SsoOutcome[] = ['success', 'created'];

/** The hub pages a Google return may land on — the API's two return paths. */
export type SsoReturnPath = typeof HUB_LOGIN_PATH | typeof HUB_REGISTER_PATH;

const SSO_RETURN_PATHS: readonly SsoReturnPath[] = [HUB_LOGIN_PATH, HUB_REGISTER_PATH];

/** Falls back to brand 1 when the host lookup produced no brand — same as `Auth.login`. */
const FALLBACK_BRAND_ID = 1;

/** 32 random bytes from `crypto.getRandomValues`, base64url-encoded without padding (43 chars). */
export function generateSsoNonce(
    source: Pick<Crypto, 'getRandomValues'> = globalThis.crypto
): string {
    const bytes = new Uint8Array(SSO_NONCE_BYTES);
    source.getRandomValues(bytes);
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * The `returnHost` sent on start: the current `host` (hostname plus port, when there is one),
 * lowercased. The API returns to `https://{returnHost}/ux/login` (sign in) or `/ux/register`
 * (sign up) — never the page the button was on, so a login form rendered inline by the studio
 * layout on a studio URL (`layout-component.tsx`) still returns to the hub login page, where the
 * return handler (`hub-app.tsx` + `hub-login-page.tsx`) lives.
 */
export function getGoogleReturnHost(loc: Pick<Location, 'host'> = window.location): string {
    return loc.host.toLowerCase();
}

/**
 * The API URL that starts the Google round trip. Built with `apiUrlFetch`, the same helper every
 * other API request uses, so it honours the host-info `apiHost` and the login page's backtick
 * server override (`apihost-override`) alike. Read at call time, not import time, because both of
 * those can change after this module loads.
 *
 * `intent=signup` is appended for sign up ONLY; a sign-in URL has no `intent` parameter.
 */
export function buildGoogleStartUrl(
    brandId: number | undefined,
    returnHost: string,
    ssoNonce: string,
    intent: GoogleSsoIntent = 'signin'
): string {
    const query = new URLSearchParams({
        brandId: String(brandId ?? FALLBACK_BRAND_ID),
        returnHost,
        [SSO_NONCE_PARAM]: ssoNonce
    });
    if (intent === 'signup') {
        query.set(SSO_INTENT_PARAM, 'signup');
    }
    return `${apiUrlFetch(GOOGLE_START_PATH)}?${query.toString()}`;
}

/**
 * Where to go after a successful Google sign-in that was started from a page that must be returned
 * to — today only the MCP consent screen, whose connection request lives in its own query string
 * (MCP-SERVER-PLAN.md §2.3b, 2026-10-03).
 *
 * ★ HELD IN THIS TAB, NOT SENT THROUGH THE API. The API returns every Google sign-in to
 * `/ux/login` and has no notion of a return path; carrying one through it would be an API change
 * and a redirect parameter on a public endpoint. `sessionStorage` is per tab and per origin, which
 * is exactly the scope of the round trip — the same reasoning as the nonce beside it.
 */
export const GOOGLE_SSO_RESUME_KEY = 'hdph.googleSsoResume';

/** The consent screen's hand-off lives ten minutes; a resume older than that has nothing to resume. */
const SSO_RESUME_LIFETIME_MS = 10 * 60_000;

/**
 * ROBO-961: a delivery page opened by its download GUID (`/ux/delivery/{guid}` or `.../{tab}`), where
 * the Edit Listing & Contact Info drawer offers sign-in to a viewer with no session.
 */
const DELIVERY_GUID_PATH_RE =
    /^\/ux\/delivery\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/[a-z0-9-]+)?$/i;

/**
 * ★ ONLY THE CONSENT SCREEN AND A GUID DELIVERY PAGE, AND ONLY AS A PATH ON THIS ORIGIN. Anything
 * else is refused rather than followed: a short allowlist keeps this from ever becoming an open
 * redirect, whatever ends up in storage. Add a page here deliberately.
 */
export function isSsoResumePath(path: string): boolean {
    if (!path.startsWith('/') || path.startsWith('//')) return false;
    const [pathname] = path.split(/[?#]/);
    const trimmed = pathname.replace(/\/+$/, '');
    return (
        trimmed.toLowerCase() === HUB_MCP_CONNECT_PATH.toLowerCase() ||
        DELIVERY_GUID_PATH_RE.test(trimmed)
    );
}

/**
 * The resume path, removed as it is read — so it is honoured at most once. Undefined when there is
 * none, it has expired, or it is not an allowed page.
 */
export function takeSsoResumePath(
    storage: Pick<Storage, 'getItem' | 'removeItem'> | undefined = sessionStorageOrUndefined(),
    now: number = Date.now()
): string | undefined {
    let raw: string | null = null;
    try {
        raw = storage?.getItem(GOOGLE_SSO_RESUME_KEY) ?? null;
        storage?.removeItem(GOOGLE_SSO_RESUME_KEY);
    } catch {
        return undefined;
    }
    if (!raw) return undefined;
    try {
        const { path, expiresAt } = JSON.parse(raw) as { path?: unknown; expiresAt?: unknown };
        if (typeof path !== 'string' || typeof expiresAt !== 'number') return undefined;
        if (expiresAt < now || !isSsoResumePath(path)) return undefined;
        return path;
    } catch {
        return undefined;
    }
}

/** Everything {@link startGoogleSignIn} touches in the browser, injectable for tests. */
export interface StartGoogleSignInOptions {
    /**
     * A page on this origin to come back to after a successful sign-in, instead of wherever
     * `/ux/login` would send the user. Only the consent screen is honoured (`isSsoResumePath`).
     */
    resumeTo?: string;
    /** `'signin'` (default) from the login page, `'signup'` from the registration page. */
    intent?: GoogleSsoIntent;
    /** `window.location` is not spy-able in jsdom. */
    assign?: (url: string) => void;
    /** Where `returnHost` is read from; defaults to `window.location`. */
    loc?: Pick<Location, 'host'>;
    storage?: Pick<Storage, 'setItem'>;
    cryptoSource?: Pick<Crypto, 'getRandomValues'>;
}

/**
 * Starts the redirect: generates the nonce, resolves the host's brand the same way the password
 * login does (`Api.login` → `ensureHostInfo()`, which also applies the brand's `apiHost`, so it is
 * awaited before the URL is built), stores the nonce, then navigates.
 *
 * REJECTS rather than navigating when the nonce cannot be stored (sessionStorage blocked or full):
 * a round trip started without a stored nonce can only ever come back `failed`, so the button shows
 * the error straight away instead.
 */
export async function startGoogleSignIn({
    intent = 'signin',
    resumeTo,
    assign = url => window.location.assign(url),
    loc = window.location,
    storage,
    cryptoSource = globalThis.crypto
}: StartGoogleSignInOptions = {}): Promise<void> {
    const nonce = generateSsoNonce(cryptoSource);
    const hostInfo = await ensureHostInfo();
    const url = buildGoogleStartUrl(hostInfo?.brandId, getGoogleReturnHost(loc), nonce, intent);
    const tab = storage ?? window.sessionStorage;
    tab.setItem(GOOGLE_SSO_NONCE_KEY, nonce);
    if (resumeTo && isSsoResumePath(resumeTo)) {
        tab.setItem(
            GOOGLE_SSO_RESUME_KEY,
            JSON.stringify({ path: resumeTo, expiresAt: Date.now() + SSO_RESUME_LIFETIME_MS })
        );
    }
    assign(url);
}

/** An unrecognised non-empty value is treated as a failure — it is still a Google return. */
export function parseSsoOutcome(value: string | null | undefined): SsoOutcome | undefined {
    if (!value) return undefined;
    return (SSO_OUTCOMES as readonly string[]).includes(value) ? (value as SsoOutcome) : 'failed';
}

/** The parts of `window.location` {@link consumeGoogleSsoReturn} reads. */
export type SsoReturnLocation = LocationLike & Pick<Location, 'hash' | 'pathname' | 'search'>;

function sessionStorageOrUndefined(): Pick<Storage, 'getItem' | 'removeItem'> | undefined {
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
}

/** The return path `pathname` is (trailing slash and case tolerated), or `undefined`. */
function matchReturnPath(pathname: string): SsoReturnPath | undefined {
    const normalized = pathname.replace(/\/+$/, '').toLowerCase();
    return SSO_RETURN_PATHS.find(path => path.toLowerCase() === normalized);
}

let pending: { outcome: SsoOutcome; path: SsoReturnPath } | undefined;

/**
 * Reads a Google return off the URL fragment, validates it against this tab's stored nonce, and
 * banks the outcome for the page it landed on. Called once, at hub module scope (`hub-app.tsx`).
 *
 * Only a fragment that carries `sso` on `/ux/login` or `/ux/register` is treated as a return;
 * anything else (no fragment, an unrelated `#hash`, any other path, a bare `?SessionKey=` query)
 * is left completely alone and returns `undefined` — nothing is persisted, stripped or consumed.
 *
 * For a return:
 *   - The stored nonce is read and REMOVED, whatever the outcome — one nonce, one return.
 *   - The outcome is accepted only when `ssoNonce` equals the stored nonce, compared exactly. A
 *     missing, empty or different nonce on either side makes it `failed`.
 *   - The SessionKey is persisted (cookie + localStorage, via `persistSessionKey`) only for a
 *     nonce-matched `success` or `created` with a well-formed key. Either of those without one is
 *     `failed`, so the page never authorizes against some older cookie instead.
 *   - The whole fragment is stripped with `replaceState` (after persisting, so the key is never
 *     held only by the address bar), leaving no history entry and nothing for a refresh to replay.
 *
 * The outcome is not checked against the path: which outcomes make sense on which page is the
 * pages' business (`hub-login-page.tsx`, `hub-register-page.tsx`).
 */
export function consumeGoogleSsoReturn(
    loc: SsoReturnLocation = window.location,
    history: Pick<History, 'state' | 'replaceState'> = window.history,
    storage: Pick<Storage, 'getItem' | 'removeItem'> | undefined = sessionStorageOrUndefined(),
    persist: (sessionKey: string, loc: LocationLike) => void = persistSessionKey
): SsoOutcome | undefined {
    const fragment = loc.hash.startsWith('#') ? loc.hash.slice(1) : loc.hash;
    const path = matchReturnPath(loc.pathname);
    if (!fragment || !path) {
        return undefined;
    }
    const params = new URLSearchParams(fragment);
    if (!params.has(SSO_PARAM)) {
        return undefined;
    }

    let storedNonce: string | null = null;
    try {
        storedNonce = storage?.getItem(GOOGLE_SSO_NONCE_KEY) ?? null;
        storage?.removeItem(GOOGLE_SSO_NONCE_KEY);
    } catch {
        storedNonce = null;
    }
    const returnedNonce = params.get(SSO_NONCE_PARAM);
    const isNonceMatch = Boolean(storedNonce) && returnedNonce === storedNonce;

    let outcome: SsoOutcome = isNonceMatch
        ? (parseSsoOutcome(params.get(SSO_PARAM)) ?? 'failed')
        : 'failed';

    if (SESSION_OUTCOMES.includes(outcome)) {
        const sessionKey = params.get(SESSION_KEY_PARAM);
        if (sessionKey && SESSION_KEY_RE.test(sessionKey)) {
            persist(sessionKey, loc);
        } else {
            outcome = 'failed';
        }
    }

    history.replaceState(history.state, '', `${loc.pathname}${loc.search}`);

    pending = { outcome, path };
    return outcome;
}

/**
 * The outcome consumed on this document load, until {@link clearSsoReturnOutcome} is called.
 *
 * Pass the asking page's path to get only a return that landed on THAT page: the login page's
 * `no-account` navigates to registration within the same document, and the register page must not
 * read the login page's outcome as its own.
 */
export function getSsoReturnOutcome(path?: SsoReturnPath): SsoOutcome | undefined {
    if (!pending || (path !== undefined && pending.path !== path)) {
        return undefined;
    }
    return pending.outcome;
}

/** Called once a page has acted on the outcome, so a later in-app visit does not replay it. */
export function clearSsoReturnOutcome() {
    pending = undefined;
}
